/**
 * Derive a flat, severity-ranked list of model issues from a probe report.
 *
 * This is the single source of truth for "what's wrong (or noteworthy) with
 * this model", independent of any UI or optimization profile. It folds together
 * facts the probe already measured — texture dimensions, declared extensions,
 * scene activation, world scale, per-material hints — so callers (the editor,
 * headless validation, the AI gym, upload gating) all agree on the same list.
 *
 * Profile-relative judgments (over the file-size or vertex budget) live in
 * `evaluateAgainstProfile`; this module only reports facts intrinsic to the
 * model itself.
 */

import type { IssueSeverity, ModelIssue, ModelProbeReport } from './types';
import { isNonPowerOfTwo, rateExtension } from './extensions';

/** Longest side (in world units) past which a model is "large" for AR. */
export const AR_OVERSIZED_UNITS = 5;
/** Longest side below which a model is "tiny" for AR. */
export const AR_TINY_UNITS = 0.01;
/** Morph-target data is flagged when it is at least this share of the file, and this large. */
export const MORPH_HEAVY_SHARE = 0.4;
export const MORPH_HEAVY_MIN_BYTES = 1024 * 1024;

const SEVERITY_RANK: Record<IssueSeverity, number> = { error: 0, warning: 1, info: 2 };

/**
 * Rate each declared extension against what our runtimes actually implement.
 *
 * `extensionsRequired` is the severity multiplier. The glTF spec obliges a
 * client that doesn't implement a REQUIRED extension to refuse the file, so an
 * unverified required extension isn't "may look wrong" — it's "may not open".
 * The same extension listed as merely used degrades gracefully and stays a
 * warning (or info, for one we simply haven't rated).
 */
function extensionIssues(report: ModelProbeReport): ModelIssue[] {
  const issues: ModelIssue[] = [];
  const required = new Set(report.extensionsRequired);

  for (const name of report.extensionsUsed) {
    const { support, note } = rateExtension(name);
    if (support === 'supported') continue;

    if (support === 'unsupported') {
      issues.push({ severity: 'error', code: 'extension-unsupported', title: 'Unsupported extension', detail: note, subject: name });
    } else if (required.has(name)) {
      issues.push({
        severity: 'error',
        code: 'extension-required-unverified',
        title: 'Required extension may not be supported',
        detail: `This file declares ${name} as required, so a runtime without it must refuse to load the model at all — not just render it differently. ${note}`,
        subject: name,
      });
    } else if (support === 'partial') {
      issues.push({ severity: 'warning', code: 'extension-unverified', title: 'Partially supported extension', detail: note, subject: name });
    } else {
      issues.push({ severity: 'info', code: 'extension-unverified', title: 'Unverified extension', detail: note, subject: name });
    }
  }

  return issues;
}

/**
 * Build the issues list for a probed model, sorted worst-first (errors, then
 * warnings, then info). Returns an empty array for a clean model.
 */
export function collectIssues(report: ModelProbeReport): ModelIssue[] {
  const issues: ModelIssue[] = [];

  // --- Analysis bailout -----------------------------------------------------
  // A bailed probe reports zeros for every measurement, which reads as a clean,
  // tiny model unless we say otherwise. Lead with the failure.
  if (report.bailout) {
    issues.push({
      severity: 'error',
      code: 'analysis-failed',
      title: 'Could not analyze this model',
      detail:
        report.warnings.find((w) => w.code === report.bailout)?.message ??
        `Analysis stopped (${report.bailout}). The stats below are not measurements — treat them as unknown.`,
    });
    // Everything past this point reads measurements the probe never took — a
    // zeroed report would claim "no scene defined" and a 0-unit bounding box
    // about a file we couldn't open. Extensions are the exception: they come
    // from the JSON header, which parses even when the buffers don't.
    issues.push(...extensionIssues(report));
    return issues.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
  }

  // --- Scene activation -----------------------------------------------------
  const { sceneCount, hasDefaultScene } = report.sceneInfo;
  if (sceneCount === 0) {
    issues.push({
      severity: 'error',
      code: 'no-scenes',
      title: 'No scene defined',
      detail: 'The file contains no scene, so there is nothing for a viewer to display.',
    });
  } else if (!hasDefaultScene) {
    issues.push({
      severity: 'error',
      code: 'no-active-scene',
      title: 'No active scene set',
      detail: `The file defines ${sceneCount === 1 ? 'a scene' : `${sceneCount} scenes`} but doesn't mark one active (no top-level "scene" index). Some viewers render nothing; optimizing sets the first scene active.`,
    });
  }

  // --- Textures: non-power-of-two ------------------------------------------
  for (const tex of report.textures.textures) {
    if (isNonPowerOfTwo(tex.width, tex.height)) {
      issues.push({
        severity: 'warning',
        code: 'texture-npot',
        title: 'Non-power-of-two texture',
        detail: `${tex.width}×${tex.height} can't use mipmaps or repeat wrapping on some GPUs and may be padded to the next power of two in memory. Resize to POT (e.g. 1024×512).`,
        subject: tex.name,
      });
    }
  }

  issues.push(...extensionIssues(report));

  // --- Morph targets dominating file size -----------------------------------
  const { morphTargetCount = 0, morphTargetBytes = 0 } = report.geometry;
  if (
    morphTargetBytes >= MORPH_HEAVY_MIN_BYTES &&
    morphTargetBytes >= report.fileSize * MORPH_HEAVY_SHARE
  ) {
    const mb = (morphTargetBytes / (1024 * 1024)).toFixed(1);
    const pct = Math.round((morphTargetBytes / report.fileSize) * 100);
    issues.push({
      severity: 'warning',
      code: 'morph-targets-heavy',
      title: 'Morph targets take a large share of this file',
      detail: `${morphTargetCount} morph targets take ${mb} MB (${pct}% of the file). This often comes from baking an animation as one shape per frame. Bake fewer frames, or use a texture or shader animation instead.`,
    });
  }

  // --- World scale (AR convention: 1 unit = 1 metre) ------------------------
  const [minX, minY, minZ, maxX, maxY, maxZ] = report.boundingBox;
  const longest = Math.max(maxX - minX, maxY - minY, maxZ - minZ);
  if (longest > AR_OVERSIZED_UNITS) {
    issues.push({
      severity: 'warning',
      code: 'scale-oversized',
      title: 'Large for AR',
      detail: `Longest side is ${longest.toFixed(2)} units (= metres in AR). If the model isn't authored in metres, rescale it down.`,
    });
  } else if (longest > 0 && longest < AR_TINY_UNITS) {
    issues.push({
      severity: 'warning',
      code: 'scale-tiny',
      title: 'Tiny for AR',
      detail: `Longest side is ${longest.toFixed(4)} units (= metres in AR). If the model isn't authored in metres, rescale it up.`,
    });
  }

  // --- Per-material hints (alpha mode, double-sided, color format) ----------
  for (const mat of report.materials.materials ?? []) {
    for (const hint of mat.hints) {
      issues.push({
        severity: hint.severity === 'warn' ? 'warning' : 'info',
        code: 'material-hint',
        title: hint.title,
        detail: hint.detail,
        subject: mat.name,
      });
    }
  }

  return issues.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

/** The worst severity present in an issues list, or null if there are none. */
export function worstSeverity(issues: ModelIssue[]): IssueSeverity | null {
  if (issues.length === 0) return null;
  return issues.reduce<IssueSeverity>(
    (worst, i) => (SEVERITY_RANK[i.severity] < SEVERITY_RANK[worst] ? i.severity : worst),
    'info',
  );
}
